// Brand ↔ creator messaging data layer.
//
// Talks straight to Supabase with the brand user's JWT — RLS (see
// creator-app migration 20260624000007_messaging_schema.sql) enforces that a
// brand only sees its own conversations and that only admin/editor members can
// send. Conversations are born from DB triggers (application submitted, invite
// sent, or hire confirmed — one thread per campaign since migration
// 20260710150000), never created here. Broadcasts go through the
// send_broadcast() RPC so the fan-out runs server-side in one transaction.
import { supabase } from './supabase'
import { notifyNavBadgesStale } from './navBadges'
import { readBrandCampaigns } from './brandCampaigns'
import { BMC_FEE_PCT } from './pricing'
import { getCampaignImages } from './utils'
import { campaignProductName, type CampaignProductSource } from './campaignProduct'

export type ConversationStatus = 'active' | 'archived'
export type SenderType = 'brand' | 'creator' | 'system'

export interface MsgCreator {
  id: string
  name: string | null
  tiktok_username: string | null
  tiktok_url: string | null
  avatar_url: string | null
  creator_gmv: number | null // last-28-day GMV (roster_creators.creator_gmv)
  // TikTok GMV range string (e.g. "$1K-$10K"), shown in place of the 28-day GMV
  // figure for creators whose sales are private — i.e. whose creator_gmv is the
  // GMV_RANGE_SENTINEL_DOLLARS sentinel. Same treatment as the roster cards.
  gmv_revenue_range: string | null
  // Shoppable videos in the last 28 days (roster_creators.json->ec_video_count).
  // Divides creator_gmv into the "GMV / video" header pill — computed the same
  // way as the Applications card rather than reading the stored
  // roster_creators.gmv_per_video column, which can fall out of sync.
  ec_video_count: number | null
  // Stored roster_creators.gmv_per_video — the FALLBACK for the pill when
  // ec_video_count is missing (~10% of creators with GMV), so those threads
  // still show a figure instead of dropping the pill.
  gmv_per_video: number | null
}

export interface Attachment {
  url: string          // public/preview URL or object URL
  path?: string        // storage path (set for real uploads)
  name: string
  mime: string
  size: number
  kind: 'image' | 'file'
  width?: number
  height?: number
}

// Per-campaign progress for a creator, used by the stats strip + list pip.
export interface CampaignStat {
  campaignId: string
  name: string
  videosDone: number
  videosRequired: number
  gmvCents: number
}

export interface Conversation {
  id: string
  brand_id: string
  creator_id: string
  campaign_id: string | null // null = legacy combined (pre-per-job) thread
  status: ConversationStatus
  last_message_at: string | null
  last_message_preview: string | null
  last_message_sender_type: SenderType | null
  last_human_message_at: string | null
  pinned_at: string | null
  priority_at: string | null    // brand-side "actively negotiating" tag, independent of pin
  ended_at: string | null       // job cancelled/withdrawn (tag shown until re-engaged)
  ended_reason: string | null   // withdrawn|cancelled|declined|rejected|expired|failed
  ended_by: string | null       // creator|brand
  created_at: string
  updated_at: string
  creator?: MsgCreator | null
  campaign?: Pick<CampaignLite, 'id' | 'name' | 'product_name'> | null
}

// One proposed/committed rate, snapshotted onto a seed message at the moment
// it was created. Applications carry an array with one entry per package
// size the creator offered; invites/hires carry a single-entry array. See
// migration 20260806050000_cr_rate_changes.sql.
export interface RateSnapshotEntry {
  package_size: number | null
  proposed_rate_cents: number | null
  // Only ever present on an application's snapshot — invites/hires don't
  // have a "with usage rights" tier.
  proposed_rate_with_rights_cents?: number | null
}

export interface Message {
  id: string
  conversation_id: string
  sender_type: SenderType
  sender_user_id: string | null
  body: string
  attachments: Attachment[]
  broadcast_id: string | null
  campaign_id: string | null
  created_at: string
  edited_at: string | null
  is_deleted: boolean
  deleted_at: string | null
  // True for messages the platform sent on the brand's behalf (e.g. the
  // deadline-nudge reminder) — stored with sender_type: 'brand' so it still
  // trips the creator's normal new-message notification, but this is the
  // explicit signal that a teammate didn't type it. See migration
  // 20260806020000_cr_messages_by_platform.sql.
  by_platform: boolean
  // Which automated message this is, when by_platform is true — 'deadline_nudge'
  // today; null for every ordinary brand/creator message. Lets a future second
  // automated category get its own card/copy without overloading by_platform.
  message_category: string | null
  // The rate(s) live at the moment this seed was created — set on
  // "Application submitted"/"Invite sent"/"Hire confirmed" seeds only, null
  // on every ordinary message. See migration 20260806050000_cr_rate_changes.sql.
  rate_snapshot: RateSnapshotEntry[] | null
  // Which cr_applications / cr_hires row this seed's rate_snapshot came from —
  // set on exactly one of the two for a rate-carrying seed, both null
  // otherwise. Lets two seeds be compared ONLY when they share the same
  // source row, instead of guessing from body text + position. See migration
  // 20260807010000_cr_messages_rate_change_ids.sql.
  application_id: string | null
  hire_id: string | null
  // Set on the message a counter offer posts into the thread. The row itself
  // is embedded (see MESSAGE_SELECT) rather than re-fetched per message: the
  // card needs the terms, the note and the live status, and a thread with a
  // long negotiation would otherwise fire one query per round.
  counter_offer_id: string | null
  counter_offer: CounterOfferLite | null
}

/**
 * The subset of cr_counter_offers a thread card renders. Deliberately a
 * separate, narrower type from lib/counterOffers' CounterOffer: this one
 * arrives via a PostgREST embed on every message fetch, so widening it widens
 * every thread load.
 */
export interface CounterOfferLite {
  id: string
  sender: 'brand' | 'creator'
  round: number
  package_size: number
  brand_rate_cents: number
  creator_rate_cents: number
  with_rights: boolean
  note: string | null
  status: 'pending' | 'accepted' | 'declined' | 'countered' | 'withdrawn' | 'expired'
  decline_reason: string | null
  application_id: string | null
  hire_id: string | null
  created_at: string
}

/**
 * Columns pulled for every message in a thread. `*` plus the counter-offer
 * embed — one round trip whether or not the thread contains a negotiation
 * (PostgREST returns counter_offer: null for messages without one).
 */
const MESSAGE_SELECT =
  '*, counter_offer:cr_counter_offers!cr_messages_counter_offer_id_fkey(id,sender,round,package_size,brand_rate_cents,creator_rate_cents,with_rights,note,status,decline_reason,application_id,hire_id,created_at)'

export interface CampaignLite {
  id: string
  name: string
  product_name: string
  status: string
}

export interface HireLink {
  id?: string // present from fetchHireLinks; used to look up submission counts
  creator_id: string
  campaign_id: string
  status: string
  videos_required: number
  videos_detected: number
  brand_rate_cents?: number | null // brand-facing hired rate (incl. BMC fee)
  created_at?: string | null // used only to order latest-first (re-hire case)
}

export interface BroadcastResult {
  broadcast_id: string
  recipient_count: number
}

// Per-user read state for a conversation: the read cursor + a manual unread flag.
export interface ReadState {
  last_read_at: string | null
  manually_unread: boolean
}

export const MAX_MESSAGE_LEN = 4000

const CONV_SELECT =
  '*, creator:roster_creators(id, name, tiktok_username, tiktok_url, avatar_url, creator_gmv, gmv_revenue_range, gmv_per_video, ec_video_count:json->ec_video_count), campaign:cr_campaigns(id, name, product_name)'

// ── Reads ────────────────────────────────────────────────────────────────────

export async function fetchConversations(brandId: string): Promise<Conversation[]> {
  if (isPreviewMode()) return mockSortedConversations()
  const { data, error } = await supabase
    .from('cr_conversations')
    .select(CONV_SELECT)
    .eq('brand_id', brandId)
    .eq('status', 'active')
    // Show every active thread, including apply/invite threads that so far only
    // have the system seed — they open as soon as a creator applies or the
    // brand invites (see migration 20260715000004).
    .order('pinned_at', { ascending: false, nullsFirst: false }) // pinned first
    .order('last_message_at', { ascending: false, nullsFirst: false })
  if (error) throw error
  return (data ?? []) as Conversation[]
}

// Archived threads (status='archived'), for the Archived view.
export async function fetchArchivedConversations(brandId: string): Promise<Conversation[]> {
  if (isPreviewMode()) return mockConversations.filter((c) => c.status === 'archived')
  const { data, error } = await supabase
    .from('cr_conversations')
    .select(CONV_SELECT)
    .eq('brand_id', brandId)
    .eq('status', 'archived')
    .order('last_message_at', { ascending: false, nullsFirst: false })
  if (error) throw error
  return (data ?? []) as Conversation[]
}

export const CONV_PAGE_SIZE = 25

// One page of active threads (inbox infinite scroll). Stable `id` tiebreaker
// keeps ranged pages from drifting when last_message_at ties.
export async function fetchConversationsPage(brandId: string, offset: number, limit = CONV_PAGE_SIZE): Promise<Conversation[]> {
  if (isPreviewMode()) return offset === 0 ? mockSortedConversations() : []
  const { data, error } = await supabase
    .from('cr_conversations')
    .select(CONV_SELECT)
    .eq('brand_id', brandId)
    .eq('status', 'active')
    .order('pinned_at', { ascending: false, nullsFirst: false })
    .order('last_message_at', { ascending: false, nullsFirst: false })
    .order('id', { ascending: true })
    .range(offset, offset + limit - 1)
  if (error) throw error
  return (data ?? []) as Conversation[]
}

export async function fetchArchivedConversationsPage(brandId: string, offset: number, limit = CONV_PAGE_SIZE): Promise<Conversation[]> {
  if (isPreviewMode()) return offset === 0 ? mockConversations.filter((c) => c.status === 'archived') : []
  const { data, error } = await supabase
    .from('cr_conversations')
    .select(CONV_SELECT)
    .eq('brand_id', brandId)
    .eq('status', 'archived')
    .order('last_message_at', { ascending: false, nullsFirst: false })
    .order('id', { ascending: true })
    .range(offset, offset + limit - 1)
  if (error) throw error
  return (data ?? []) as Conversation[]
}

// ── Thread index ─────────────────────────────────────────────────────────────
// One row per thread, no joins. The inbox lists one row per CREATOR while the
// DB keeps a separate thread per job, so a row has to account for every thread
// its creator has — including ones far below the current page of hydrated rows.
// The per-creator unread badge, the job count and the thread pane's job strip
// all read this index. None of CONV_SELECT's joins are needed for that: the
// creator is the same for every thread in a group, and campaign names come from
// fetchBrandCampaigns, which already loads the brand's campaigns in full.
export interface ConvStub {
  id: string
  creator_id: string
  campaign_id: string | null
  status: ConversationStatus
  last_message_at: string | null
  last_message_sender_type: SenderType | null
  last_human_message_at: string | null
  pinned_at: string | null
  priority_at: string | null
  ended_at: string | null
  ended_reason: string | null
  ended_by: string | null
  created_at: string
  updated_at: string
}

// One literal string (not a concatenation) so supabase-js can still infer the
// row type from the column list.
const STUB_SELECT = 'id, creator_id, campaign_id, status, last_message_at, last_message_sender_type, last_human_message_at, pinned_at, priority_at, ended_at, ended_reason, ended_by, created_at, updated_at'
const STUB_PAGE = 1000
const STUB_MAX_PAGES = 20 // 20k threads — a backstop, not an expected ceiling

// Every thread (active AND archived) for the brand. Paged internally so a
// PostgREST row cap can't quietly truncate the index the grouping depends on —
// a half-loaded index would under-count a creator's unread messages.
export async function fetchConversationStubs(brandId: string): Promise<ConvStub[]> {
  if (isPreviewMode()) return mockConversations.map(conversationToStub)
  const all: ConvStub[] = []
  for (let page = 0; page < STUB_MAX_PAGES; page++) {
    const from = page * STUB_PAGE
    const { data, error } = await supabase
      .from('cr_conversations')
      .select(STUB_SELECT)
      .eq('brand_id', brandId)
      .order('last_message_at', { ascending: false, nullsFirst: false })
      .order('id', { ascending: true })
      .range(from, from + STUB_PAGE - 1)
    if (error) throw error
    const rows = (data ?? []) as ConvStub[]
    all.push(...rows)
    if (rows.length < STUB_PAGE) break
  }
  return all
}

// Narrow a hydrated row to its index shape — for a thread that reaches the UI
// through the paged list (or a realtime insert) before the index catches up.
export function conversationToStub(c: Conversation): ConvStub {
  return {
    id: c.id, creator_id: c.creator_id, campaign_id: c.campaign_id, status: c.status,
    last_message_at: c.last_message_at, last_message_sender_type: c.last_message_sender_type,
    last_human_message_at: c.last_human_message_at, pinned_at: c.pinned_at, priority_at: c.priority_at,
    ended_at: c.ended_at, ended_reason: c.ended_reason, ended_by: c.ended_by,
    created_at: c.created_at, updated_at: c.updated_at,
  }
}

// Get-or-create the thread for (brand, creator, campaign). Legacy apps/invites
// have no thread yet; the "Message" button uses this so it always opens one.
// Returns the conversation id (or null on error / preview).
export async function getOrCreateConversation(
  brandId: string, creatorId: string, campaignId: string | null,
): Promise<string | null> {
  if (isPreviewMode()) return null
  const { data, error } = await supabase.rpc('cr_get_or_create_conversation', {
    p_brand_id: brandId, p_creator_id: creatorId, p_campaign_id: campaignId,
  })
  if (error) return null
  return (data as string) ?? null
}

// Hydrate full conversation rows for a set of ids (for server search hits).
export async function fetchConversationsByIds(ids: string[]): Promise<Conversation[]> {
  if (ids.length === 0) return []
  if (isPreviewMode()) return mockConversations.filter((c) => ids.includes(c.id))
  const { data, error } = await supabase.from('cr_conversations').select(CONV_SELECT).in('id', ids)
  if (error) throw error
  return (data ?? []) as Conversation[]
}

// Server-side global search (creator name/handle, job, message text) across ALL
// threads — not just what's paged in. Returns conversations in server order plus
// a snippet map for message-text matches. Paginated for search infinite scroll.
export async function searchConversations(
  brandId: string, term: string, offset = 0, limit = CONV_PAGE_SIZE,
): Promise<{ conversations: Conversation[]; snippets: Record<string, string> }> {
  const q = term.trim()
  if (q.length < 2) return { conversations: [], snippets: {} }
  if (isPreviewMode()) {
    const hits = await searchMessages(q)
    const snippets: Record<string, string> = {}
    for (const h of hits) snippets[h.conversation_id] = h.snippet
    const ql = q.toLowerCase()
    const conversations = mockSortedConversations().filter((c) => {
      const name = creatorDisplayName(c.creator).toLowerCase()
      const handle = (c.creator?.tiktok_username ?? '').toLowerCase()
      const job = (conversationJobName(c) ?? '').toLowerCase()
      return name.includes(ql) || handle.includes(ql) || job.includes(ql) || !!snippets[c.id]
    })
    return { conversations: conversations.slice(offset, offset + limit), snippets }
  }
  const { data, error } = await supabase.rpc('search_brand_conversations', {
    p_brand_id: brandId, p_term: q, p_limit: limit, p_offset: offset,
  })
  if (error) throw error
  const rows = (data ?? []) as { conversation_id: string; snippet: string | null }[]
  const ids = rows.map((r) => r.conversation_id)
  const snippets: Record<string, string> = {}
  for (const r of rows) if (r.snippet) snippets[r.conversation_id] = r.snippet
  const byId = new Map((await fetchConversationsByIds(ids)).map((c) => [c.id, c]))
  const conversations = ids.map((id) => byId.get(id)).filter(Boolean) as Conversation[]
  return { conversations, snippets }
}

// Open a thread by creator (used by the "Message" deep-links on Hired Creators
// and Applications). Bypasses the inbox's human-message filter, since the brand
// may be starting a thread with a creator they've never messaged. A creator can
// have several per-job threads with one brand, so pass the campaignId to get
// that job's thread; falls back to the legacy combined (campaign-NULL) thread,
// then to the most recent thread.
export async function fetchConversationByCreator(
  brandId: string,
  creatorId: string,
  campaignId?: string | null,
): Promise<Conversation | null> {
  const pick = (rows: Conversation[]): Conversation | null => {
    if (campaignId) {
      return rows.find((c) => c.campaign_id === campaignId)
        ?? rows.find((c) => c.campaign_id === null)
        ?? null
    }
    return rows.find((c) => c.campaign_id === null) ?? rows[0] ?? null
  }
  if (isPreviewMode()) return pick(mockConversations.filter((c) => c.creator_id === creatorId))
  const { data, error } = await supabase
    .from('cr_conversations')
    .select(CONV_SELECT)
    .eq('brand_id', brandId)
    .eq('creator_id', creatorId)
    .order('last_message_at', { ascending: false, nullsFirst: false })
  if (error) throw error
  return pick((data ?? []) as Conversation[])
}

export async function fetchMessages(conversationId: string): Promise<Message[]> {
  if (isPreviewMode()) return [...(mockMessages[conversationId] ?? [])]
  const { data, error } = await supabase
    .from('cr_messages')
    .select(MESSAGE_SELECT)
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return resignAttachmentUrls((data ?? []) as Message[])
}

export const MSG_PAGE_SIZE = 100

// One page of a thread's messages, NEWEST-first (for load-older infinite scroll).
// offset 0 = most recent page. Returned newest→oldest; the caller reverses each
// page to ascending and prepends older pages above what's already shown.
export async function fetchMessagesPage(conversationId: string, offset: number, limit = MSG_PAGE_SIZE): Promise<Message[]> {
  if (isPreviewMode()) return [...(mockMessages[conversationId] ?? [])].reverse().slice(offset, offset + limit)
  const { data, error } = await supabase
    .from('cr_messages')
    .select(MESSAGE_SELECT)
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(offset, offset + limit - 1)
  if (error) throw error
  return resignAttachmentUrls((data ?? []) as Message[])
}

// Per-user read state → { conversation_id: { last_read_at, manually_unread } }.
export async function fetchReadCursors(userId: string): Promise<Record<string, ReadState>> {
  if (isPreviewMode()) return { ...mockReadStates }
  const { data, error } = await supabase
    .from('cr_conversation_reads')
    .select('conversation_id, last_read_at, manually_unread')
    .eq('user_id', userId)
  if (error) throw error
  const map: Record<string, ReadState> = {}
  for (const r of (data ?? []) as { conversation_id: string; last_read_at: string | null; manually_unread: boolean }[]) {
    map[r.conversation_id] = { last_read_at: r.last_read_at, manually_unread: r.manually_unread }
  }
  return map
}

const UNREAD_COUNT_CHUNK = 60

// Per-thread count of messages the brand hasn't read — the number behind the
// inbox's per-creator badge and each job chip in the thread pane. Only threads
// isUnread() already flags get queried: nearly every thread in an inbox is read,
// and counting those would be pure waste. Two shapes:
//   • a thread with a read cursor — one batched query per chunk, narrowed by the
//     OLDEST cursor in the chunk purely to keep the payload small, then tallied
//     against each thread's own cursor;
//   • a thread never opened (no cursor) — every creator message counts.
// Only creator messages count, matching isUnread: the brand's own sends and the
// system seeds never make a thread unread. A manual "mark as unread" can land on
// a thread with nothing new, so 0 is a legitimate answer for an unread thread —
// callers fall back to a plain dot there rather than printing a zero.
export async function fetchUnreadMessageCounts(
  convos: UnreadShape[],
  states: Record<string, ReadState>,
): Promise<Record<string, number>> {
  const counts: Record<string, number> = {}
  const targets = convos.filter((c) => isUnread(c, states))
  for (const c of targets) counts[c.id] = 0
  if (targets.length === 0) return counts
  const cursorOf = (id: string) => states[id]?.last_read_at ?? null
  if (isPreviewMode()) {
    for (const c of targets) {
      const cursor = cursorOf(c.id)
      counts[c.id] = (mockMessages[c.id] ?? [])
        .filter((m) => m.sender_type === 'creator' && (!cursor || m.created_at > cursor)).length
    }
    return counts
  }
  const ids = targets.map((c) => c.id)
  const tally = async (chunkIds: string[], narrow: boolean) => {
    for (let i = 0; i < chunkIds.length; i += UNREAD_COUNT_CHUNK) {
      const chunk = chunkIds.slice(i, i + UNREAD_COUNT_CHUNK)
      let q = supabase
        .from('cr_messages')
        .select('conversation_id, created_at')
        .in('conversation_id', chunk)
        .eq('sender_type', 'creator')
      if (narrow) {
        // Every id in this chunk has a cursor; the earliest is a safe floor.
        const floor = chunk.map((id) => cursorOf(id) as string).sort()[0]
        q = q.gt('created_at', floor)
      }
      const { data, error } = await q
      if (error) throw error
      for (const m of (data ?? []) as { conversation_id: string; created_at: string }[]) {
        const cursor = cursorOf(m.conversation_id)
        if (cursor && m.created_at <= cursor) continue
        counts[m.conversation_id] = (counts[m.conversation_id] ?? 0) + 1
      }
    }
  }
  await tally(ids.filter((id) => cursorOf(id)), true)
  await tally(ids.filter((id) => !cursorOf(id)), false)
  return counts
}

export async function fetchBrandCampaigns(brandId: string): Promise<CampaignLite[]> {
  if (isPreviewMode()) return [...mockCampaigns]
  // Shared with the other screens that need the same list — see
  // lib/brandCampaigns.ts. All statuses, as before: the inbox tags threads from
  // archived campaigns too.
  return (await readBrandCampaigns(brandId)) as unknown as CampaignLite[]
}

// Confirmed/completed hires power the campaign lens: a conversation belongs to
// every campaign its creator has a live hire on for this brand.
export async function fetchHireLinks(brandId: string): Promise<HireLink[]> {
  if (isPreviewMode()) return [...mockHireLinks]
  // A creator can be re-hired on the same campaign after completing one (the
  // unique index is partial, excluding terminal statuses), so a completed + a
  // new confirmed hire can coexist for one pair. Order newest-first so the
  // list's amountFor .find() returns the latest hire's rate, not a stale one.
  const { data, error } = await supabase
    .from('cr_hires')
    .select('id, creator_id, campaign_id, status, videos_required, videos_detected, brand_rate_cents, created_at')
    .eq('brand_id', brandId)
    // partial_completed = was 'completed', then an admin rejected/deleted a
    // video and the approved count dropped below videos_required — still a
    // live hire (the creator owes a replacement), so it belongs here same as
    // confirmed/completed.
    .in('status', ['confirmed', 'completed', 'partial_completed'])
    .order('created_at', { ascending: false, nullsFirst: false })
  if (error) throw error
  return (data ?? []) as HireLink[]
}

// Count of submitted videos per hire, from cr_video_submissions — the source of
// truth for "videos done" (cr_hires.videos_detected is not bumped on submit).
// Keyed by hire_id so a re-hire on the same job counts only its own submissions.
export async function fetchVideoSubmissionCounts(brandId: string): Promise<Record<string, number>> {
  if (isPreviewMode()) return {}
  const { data, error } = await supabase
    .from('cr_video_submissions')
    .select('hire_id, review_status')
    .eq('brand_id', brandId)
    .eq('deleted', false)
  if (error || !data) return {}
  const map: Record<string, number> = {}
  for (const r of data as { hire_id: string | null; review_status: 'approved' | 'rejected' | null }[]) {
    if (!r.hire_id || r.review_status === 'rejected') continue
    map[r.hire_id] = (map[r.hire_id] ?? 0) + 1
  }
  return map
}

// Per-(creator, campaign) applied rate RANGE for the message list's "Applied
// for $X" pill. Brand-facing cents (proposed package rates + BMC fee), keyed
// `${creator_id}:${campaign_id}`. An application can carry several package
// options (e.g. 5 videos vs 10 videos), so we expose the lowest AND highest —
// showing only the min hid the other option and misrepresented the deal.
export interface AppliedRateRange { min: number; max: number; at: string | null }
export async function fetchApplicationRates(brandId: string): Promise<Record<string, AppliedRateRange>> {
  if (isPreviewMode()) return {}
  // A creator can apply, be rejected, and re-apply with different rates — each
  // re-apply INSERTS a fresh cr_applications row (the unique index is partial,
  // only on active pending/approved rows), so multiple rows can exist per
  // (creator, campaign). Order newest-first and keep ONLY the latest row per
  // pair so the "Applied for $X" pill reflects the current deal, not a stale
  // declined one.
  const { data, error } = await supabase
    .from('cr_applications')
    .select('creator_id, campaign_id, packages, last_applied_at, applied_at')
    .eq('brand_id', brandId)
    .order('last_applied_at', { ascending: false, nullsFirst: false })
    .order('applied_at', { ascending: false, nullsFirst: false })
  if (error) return {}
  const map: Record<string, AppliedRateRange> = {}
  const seen = new Set<string>()
  for (const r of (data ?? []) as { creator_id: string; campaign_id: string; packages: { proposed_rate_cents: number }[] | null; last_applied_at: string | null; applied_at: string | null }[]) {
    const key = `${r.creator_id}:${r.campaign_id}`
    if (seen.has(key)) continue // rows are newest-first — the first per pair is the latest application
    seen.add(key)
    const rates = (r.packages ?? []).map((p) => p.proposed_rate_cents).filter((n) => typeof n === 'number')
    if (!rates.length) continue
    map[key] = { min: toBrandCents(Math.min(...rates)), max: toBrandCents(Math.max(...rates)), at: r.last_applied_at ?? r.applied_at ?? null }
  }
  return map
}

// Per-(creator, campaign) LATEST live offer/hire, for resolving the status pill.
// Unlike fetchHireLinks (confirmed/completed only, powering the campaign lens),
// this includes pending invites so the pill can show a brand-sent offer that
// hasn't been accepted yet. state 'hired' = confirmed/completed, 'invited' =
// invited/waitlisted. `at` is the offer's own timestamp, used to decide whether
// the offer or the application is the more recent action on the thread.
export interface OfferLink {
  state: 'hired' | 'invited'
  amountCents: number | null
  packageSize: number | null
  at: string
  // How the hire came about. A confirmed hire seeded by an approved application
  // ('application') lives on the Applications tab — /roster/invited lists only
  // invitation-sourced hires — so the job card's CTA routes on this.
  source: 'invitation' | 'application' | null
}
export async function fetchOfferLinks(brandId: string): Promise<Record<string, OfferLink>> {
  if (isPreviewMode()) return {}
  const { data, error } = await supabase
    .from('cr_hires')
    .select('creator_id, campaign_id, status, source, brand_rate_cents, package_size, invited_at, last_invited_at, confirmed_at, created_at')
    .eq('brand_id', brandId)
    .in('status', ['invited', 'waitlisted', 'confirmed', 'completed', 'partial_completed'])
  if (error || !data) return {}
  const map: Record<string, OfferLink> = {}
  for (const r of data as { creator_id: string; campaign_id: string; status: string; source: string | null; brand_rate_cents: number | null; package_size: number | null; invited_at: string | null; last_invited_at: string | null; confirmed_at: string | null; created_at: string | null }[]) {
    const key = `${r.creator_id}:${r.campaign_id}`
    // Recency of the offer. Re-inviting after a decline PATCHes the row and
    // preserves invited_at, bumping only last_invited_at — so last_invited_at is
    // the true "latest offer" signal; confirmed_at wins once accepted.
    const at = r.confirmed_at ?? r.last_invited_at ?? r.invited_at ?? r.created_at ?? ''
    const existing = map[key]
    // Keep the most recent offer per pair.
    if (existing && new Date(existing.at).getTime() >= new Date(at).getTime()) continue
    map[key] = {
      state: r.status === 'confirmed' || r.status === 'completed' || r.status === 'partial_completed' ? 'hired' : 'invited',
      amountCents: r.brand_rate_cents ?? null,
      packageSize: r.package_size ?? null,
      at,
      source: r.source === 'application' || r.source === 'invitation' ? r.source : null,
    }
  }
  return map
}

// Job-card data for a thread's opening system message. Amounts are BRAND-FACING
// (they include the BMC platform fee), higher than the creator payout shown in
// the creator app. A hire (invite/confirmed) has one committed rate; a pending
// application carries every package option the creator proposed.
export interface JobCardOption {
  packageSize: number
  amountCents: number // brand-facing (creator rate + BMC platform fee)
}

export interface JobCard {
  productImageUrl: string | null
  productName: string | null
  campaignName: string | null
  hireAmountCents: number | null   // brand_rate_cents — already includes the fee
  hirePackageSize: number | null
  applicationOptions: JobCardOption[]
  // campaign.usage_rights_mode === 'required' — an application's rows show the
  // with-rights package price (where the creator quoted one), since that's the
  // only price the brand can approve.
  usageRightsRequired: boolean
}

// The rate a chat card shows for one proposed package. When the campaign
// requires usage rights and the creator quoted a with-rights price for this
// package, that's the price; otherwise the base rate (a proposal that predates
// the requirement never quoted rights — see proposalQuotesRights in the worker).
function packageRateCents(
  p: { proposed_rate_cents: number; proposed_rate_with_rights_cents?: number | null },
  rightsRequired: boolean,
): number {
  const wr = p.proposed_rate_with_rights_cents
  return rightsRequired && wr != null && wr > 0 ? wr : p.proposed_rate_cents
}

export const toBrandCents = (creatorCents: number): number =>
  Math.round(creatorCents * (1 + BMC_FEE_PCT / 100))

// Converts a seed message's OWN rate_snapshot (raw creator-side cents, see
// migration 20260806050000_cr_rate_changes.sql) into brand-facing
// JobCardOption[] — same toBrandCents conversion as every other brand-facing
// rate in this file, so a seed rendered from its own snapshot looks identical
// to one rendered from fetchJobCard/fetchApplicationHistory/fetchHireHistory.
//
// Why a seed would ever need its OWN snapshot instead of those fetches: all
// three read cr_applications/cr_hires LIVE, and both tables are edited IN
// PLACE (creator_update_application, handleEditInvite) with no row history —
// so once a rate changes, every fetch reflects only the new value, and an
// OLDER seed card would silently start showing the current rate instead of
// the one it was actually created with. rate_snapshot was captured at INSERT
// time specifically to give each seed an immutable value those live fetches
// can't provide.
export function rateSnapshotToOptions(snapshot: RateSnapshotEntry[], rightsRequired = false): JobCardOption[] {
  return snapshot
    .filter((e): e is RateSnapshotEntry & { package_size: number; proposed_rate_cents: number } =>
      e.package_size != null && e.proposed_rate_cents != null)
    .map((e) => ({ packageSize: e.package_size, amountCents: toBrandCents(packageRateCents({ proposed_rate_cents: e.proposed_rate_cents, proposed_rate_with_rights_cents: e.proposed_rate_with_rights_cents }, rightsRequired)) }))
}

export async function fetchJobCard(
  brandId: string,
  creatorId: string,
  campaignId: string | null,
): Promise<JobCard | null> {
  if (isPreviewMode() || !campaignId) return null
  const { data: campaign } = await supabase
    .from('cr_campaigns')
    .select('name, product_name, product_image_url, product_image_urls, is_multi_product, bundle_product_name, bundle_product_image_urls, usage_rights_mode')
    .eq('id', campaignId)
    .maybeSingle()
  const c = campaign as ({
    name: string | null
    product_name: string | null
    product_image_url: string | null
    product_image_urls: string[] | null
    // Multi-product campaigns (2026-09-14): the three above are product #1
    // only, so the job card in chat used to label a 4-product job with one
    // product's name and cover. The bundle is what the creator receives.
    bundle_product_image_urls: string[] | null
    usage_rights_mode: string | null
  } & CampaignProductSource) | null
  // Campaigns store photos in the product_image_urls array (index 0 = cover);
  // product_image_url is a legacy single column that's null for most rows, so
  // reading it alone left the job card on the fallback icon.
  const bundleCover = c?.is_multi_product ? (c.bundle_product_image_urls?.[0] ?? null) : null
  const coverImageUrl = bundleCover ?? (c ? (getCampaignImages(c)[0] ?? null) : null)

  const [{ data: hire }, { data: app }] = await Promise.all([
    supabase
      .from('cr_hires')
      .select('brand_rate_cents, package_size, created_at')
      .eq('brand_id', brandId)
      .eq('creator_id', creatorId)
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from('cr_applications')
      .select('packages, last_applied_at')
      .eq('creator_id', creatorId)
      .eq('campaign_id', campaignId)
      .order('last_applied_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
  ])

  const h = hire as { brand_rate_cents: number | null; package_size: number | null } | null
  const usageRightsRequired = c?.usage_rights_mode === 'required'
  const pkgs = ((app as { packages: { package_size: number; proposed_rate_cents: number; proposed_rate_with_rights_cents?: number | null }[] } | null)?.packages ?? [])
  const applicationOptions: JobCardOption[] = [...pkgs]
    .sort((a, b) => a.package_size - b.package_size)
    .map((p) => ({ packageSize: p.package_size, amountCents: toBrandCents(packageRateCents(p, usageRightsRequired)) }))

  return {
    productImageUrl: coverImageUrl,
    productName: campaignProductName(c),
    campaignName: c?.name ?? campaignProductName(c),
    hireAmountCents: h?.brand_rate_cents ?? null,
    hirePackageSize: h?.package_size ?? null,
    applicationOptions,
    usageRightsRequired,
  }
}

// Every application row for a (creator, campaign), oldest-first, each with its
// own proposed packages (brand-facing). Re-applying INSERTS a new row, and each
// application also drops a "New application" seed message in the thread — so the
// thread can carry several application cards. This lets each card render the
// rates from ITS submission (matched by timestamp) instead of all cards sharing
// the latest application's rates.
export interface ApplicationSnapshot {
  appliedAt: string
  options: JobCardOption[]
}
export async function fetchApplicationHistory(
  brandId: string,
  creatorId: string,
  campaignId: string | null,
): Promise<ApplicationSnapshot[]> {
  if (isPreviewMode() || !campaignId) return []
  const { data: camp } = await supabase
    .from('cr_campaigns')
    .select('usage_rights_mode')
    .eq('id', campaignId)
    .maybeSingle()
  const rightsRequired = (camp as { usage_rights_mode: string | null } | null)?.usage_rights_mode === 'required'
  const { data, error } = await supabase
    .from('cr_applications')
    .select('packages, applied_at, last_applied_at')
    .eq('brand_id', brandId)
    .eq('creator_id', creatorId)
    .eq('campaign_id', campaignId)
    .order('applied_at', { ascending: true, nullsFirst: true })
  if (error || !data) return []
  return (data as { packages: { package_size: number; proposed_rate_cents: number; proposed_rate_with_rights_cents?: number | null }[] | null; applied_at: string | null; last_applied_at: string | null }[])
    .map((r) => ({
      appliedAt: r.applied_at ?? r.last_applied_at ?? '',
      options: [...(r.packages ?? [])]
        .sort((a, b) => a.package_size - b.package_size)
        .map((p) => ({ packageSize: p.package_size, amountCents: toBrandCents(packageRateCents(p, rightsRequired)) })),
    }))
}

// Every hire/offer row for a (creator, campaign), oldest-first by invited_at,
// each with its committed brand-facing rate. Re-inviting after a decline PATCHes
// the row in place (preserving invited_at) while re-inviting after completion
// INSERTs a fresh row — so a thread can carry several "Invite sent"/"Hire
// confirmed" seed cards. This lets each show ITS hire's rate (matched by
// invited_at ≈ the seed's created_at) instead of all sharing the latest hire.
export interface HireSnapshot {
  invitedAt: string
  packageSize: number | null
  amountCents: number | null
}
export async function fetchHireHistory(
  brandId: string,
  creatorId: string,
  campaignId: string | null,
): Promise<HireSnapshot[]> {
  if (isPreviewMode() || !campaignId) return []
  const { data, error } = await supabase
    .from('cr_hires')
    .select('brand_rate_cents, package_size, invited_at, created_at')
    .eq('brand_id', brandId)
    .eq('creator_id', creatorId)
    .eq('campaign_id', campaignId)
    .order('invited_at', { ascending: true, nullsFirst: true })
  if (error || !data) return []
  return (data as { brand_rate_cents: number | null; package_size: number | null; invited_at: string | null; created_at: string | null }[])
    .map((r) => ({
      invitedAt: r.invited_at ?? r.created_at ?? '',
      packageSize: r.package_size ?? null,
      amountCents: r.brand_rate_cents ?? null,
    }))
}

// Campaign GMV per (creator, campaign): sum of the creator's video-submission
// GMV on that campaign. Keyed `${creator_id}:${campaign_id}` → gmv cents.
export async function fetchCampaignGmv(brandId: string): Promise<Record<string, number>> {
  if (isPreviewMode()) return { ...mockCampaignGmv }
  const { data, error } = await supabase
    .from('cr_video_submissions')
    .select('creator_id, campaign_id, gmv_cents')
    .eq('brand_id', brandId)
    .eq('deleted', false)
  if (error) throw error
  const map: Record<string, number> = {}
  for (const r of (data ?? []) as { creator_id: string; campaign_id: string; gmv_cents: number | null }[]) {
    if (!r.campaign_id) continue
    const k = `${r.creator_id}:${r.campaign_id}`
    map[k] = (map[k] ?? 0) + (r.gmv_cents ?? 0)
  }
  return map
}

// Full-text-ish search of message bodies (RLS already scopes to this brand's
// conversations). Returns the conversation ids that contain a match + a snippet.
export async function searchMessages(term: string): Promise<{ conversation_id: string; snippet: string }[]> {
  const q = term.trim()
  if (q.length < 2) return []
  if (isPreviewMode()) {
    const out: { conversation_id: string; snippet: string }[] = []
    for (const [convId, msgs] of Object.entries(mockMessages)) {
      const hit = msgs.find((m) => m.sender_type !== 'system' && m.body.toLowerCase().includes(q.toLowerCase()))
      if (hit) out.push({ conversation_id: convId, snippet: hit.body })
    }
    return out
  }
  const { data, error } = await supabase
    .from('cr_messages')
    .select('conversation_id, body')
    .ilike('body', `%${q}%`)
    .order('created_at', { ascending: false })
    .limit(100)
  if (error) throw error
  const seen = new Set<string>()
  const out: { conversation_id: string; snippet: string }[] = []
  for (const r of (data ?? []) as { conversation_id: string; body: string }[]) {
    if (seen.has(r.conversation_id)) continue
    seen.add(r.conversation_id)
    out.push({ conversation_id: r.conversation_id, snippet: r.body })
  }
  return out
}

export async function setPinned(conversationId: string, pinned: boolean): Promise<void> {
  if (isPreviewMode()) {
    const c = mockConversations.find((x) => x.id === conversationId)
    if (c) c.pinned_at = pinned ? new Date().toISOString() : null
    return
  }
  const { error } = await supabase
    .from('cr_conversations')
    .update({ pinned_at: pinned ? new Date().toISOString() : null })
    .eq('id', conversationId)
  if (error) throw error
}

export async function setPriority(conversationId: string, priority: boolean): Promise<void> {
  if (isPreviewMode()) {
    const c = mockConversations.find((x) => x.id === conversationId)
    if (c) c.priority_at = priority ? new Date().toISOString() : null
    return
  }
  const { error } = await supabase
    .from('cr_conversations')
    .update({ priority_at: priority ? new Date().toISOString() : null })
    .eq('id', conversationId)
  if (error) throw error
}

export async function setArchived(conversationId: string, archived: boolean): Promise<void> {
  if (isPreviewMode()) {
    const c = mockConversations.find((x) => x.id === conversationId)
    if (c) c.status = archived ? 'archived' : 'active'
    return
  }
  const { error } = await supabase
    .from('cr_conversations')
    .update({ status: archived ? 'archived' : 'active' })
    .eq('id', conversationId)
  if (error) throw error
}

// ── Bulk row actions ────────────────────────────────────────────────────────
// A creator row stands for every chat behind it, and creators here run to 65
// jobs — firing one UPDATE per chat turned a single click into 65 requests. Each
// of these is ONE request per chunk instead. Chunked because the id list rides
// in the query string.
const WRITE_CHUNK = 120

async function updateConversationsById(
  ids: string[],
  patch: Record<string, string | null>,
): Promise<void> {
  for (let i = 0; i < ids.length; i += WRITE_CHUNK) {
    const chunk = ids.slice(i, i + WRITE_CHUNK)
    const { error } = await supabase.from('cr_conversations').update(patch).in('id', chunk)
    if (error) throw error
  }
}

export async function setPinnedMany(ids: string[], pinned: boolean): Promise<void> {
  if (ids.length === 0) return
  const at = pinned ? new Date().toISOString() : null
  if (isPreviewMode()) {
    for (const c of mockConversations) if (ids.includes(c.id)) c.pinned_at = at
    return
  }
  await updateConversationsById(ids, { pinned_at: at })
}

export async function setPriorityMany(ids: string[], priority: boolean): Promise<void> {
  if (ids.length === 0) return
  const at = priority ? new Date().toISOString() : null
  if (isPreviewMode()) {
    for (const c of mockConversations) if (ids.includes(c.id)) c.priority_at = at
    return
  }
  await updateConversationsById(ids, { priority_at: at })
}

export async function setArchivedMany(ids: string[], archived: boolean): Promise<void> {
  if (ids.length === 0) return
  if (isPreviewMode()) {
    for (const c of mockConversations) if (ids.includes(c.id)) c.status = archived ? 'archived' : 'active'
    return
  }
  await updateConversationsById(ids, { status: archived ? 'archived' : 'active' })
}

// Read cursors for several threads at once — the row's "Mark as read" has to
// clear every chat behind it, or the badge (which counts them all) stays up.
export async function markConversationsRead(ids: string[], userId: string): Promise<string> {
  const at = new Date().toISOString()
  if (ids.length === 0) return at
  if (isPreviewMode()) {
    for (const id of ids) mockReadStates[id] = { last_read_at: at, manually_unread: false }
    return at
  }
  for (let i = 0; i < ids.length; i += WRITE_CHUNK) {
    const rows = ids.slice(i, i + WRITE_CHUNK).map((id) => ({
      conversation_id: id, user_id: userId, last_read_at: at, manually_unread: false,
    }))
    const { error } = await supabase
      .from('cr_conversation_reads')
      .upsert(rows, { onConflict: 'conversation_id,user_id' })
    if (error) throw error
  }
  notifyNavBadgesStale()
  return at
}

const ATTACH_BUCKET = 'message-attachments'
export const MAX_ATTACH_BYTES = 25 * 1024 * 1024 // 25 MB
const ATTACH_SIGNED_TTL = 60 * 60 * 24 * 7 // 7 days

// The bucket is private and the URL stored in the message JSON is signed with a
// 7-day TTL, so stored URLs go dead. Derive the storage path (stored for real
// uploads; parsed out of the signed URL for older messages) and re-sign on fetch.
export function attachmentStoragePath(a: Attachment): string | null {
  if (a.path) return a.path
  const m = a.url?.match(/\/object\/sign\/message-attachments\/([^?]+)/)
  return m ? m[1] : null
}

export type AttachmentKind = 'image' | 'video' | 'pdf' | 'file'

// Classify by mime (falling back to stored kind / extension) so videos and PDFs
// — both stored as kind 'file' — get proper in-app previews.
export function attachmentKind(a: Pick<Attachment, 'mime' | 'kind' | 'name'>): AttachmentKind {
  const mime = (a.mime || '').toLowerCase()
  const name = (a.name || '').toLowerCase()
  if (mime.startsWith('image/') || a.kind === 'image') return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf'
  return 'file'
}

// Force a real download. A plain <a download> is ignored cross-origin, but
// Supabase honors a `download` query param (Content-Disposition: attachment).
export function attachmentDownloadUrl(a: Pick<Attachment, 'url' | 'name'>): string {
  if (!a.url) return a.url
  const sep = a.url.includes('?') ? '&' : '?'
  return `${a.url}${sep}download=${encodeURIComponent(a.name || 'file')}`
}

async function resignAttachmentUrls(messages: Message[]): Promise<Message[]> {
  const paths = new Set<string>()
  for (const m of messages) {
    for (const a of m.attachments ?? []) {
      const p = attachmentStoragePath(a)
      if (p) paths.add(p)
    }
  }
  if (paths.size === 0) return messages
  const { data, error } = await supabase.storage
    .from(ATTACH_BUCKET)
    .createSignedUrls([...paths], ATTACH_SIGNED_TTL)
  if (error || !data) return messages // fall back to stored URLs
  const urlByPath = new Map<string, string>()
  for (const d of data) {
    if (d.path && d.signedUrl) urlByPath.set(d.path, d.signedUrl)
  }
  if (urlByPath.size === 0) return messages
  return messages.map((m) => {
    if (!m.attachments?.length) return m
    const attachments = m.attachments.map((a) => {
      const p = attachmentStoragePath(a)
      const url = p ? urlByPath.get(p) : undefined
      return url ? { ...a, url } : a
    })
    return { ...m, attachments }
  })
}

// Pixel dimensions of an image file, recorded on the attachment so both
// clients can render it at its real aspect ratio (the creator app needs
// explicit dims for RN <Image>). Best-effort — non-images/decode failures
// just omit them.
async function imageFileDims(file: File): Promise<{ width?: number; height?: number }> {
  if (!file.type.startsWith('image/')) return {}
  try {
    const bmp = await createImageBitmap(file)
    const dims = { width: bmp.width, height: bmp.height }
    bmp.close()
    return dims
  } catch {
    return {}
  }
}

// Upload a file to the conversation's attachment folder; returns the Attachment
// record to embed in the message. In preview, returns a local object URL.
export async function uploadAttachment(conversationId: string, file: File): Promise<Attachment> {
  const kind: Attachment['kind'] = file.type.startsWith('image/') ? 'image' : 'file'
  const dims = await imageFileDims(file)
  if (isPreviewMode()) {
    return { url: URL.createObjectURL(file), name: file.name, mime: file.type, size: file.size, kind, ...dims }
  }
  const path = `${conversationId}/${Date.now()}-${file.name.replace(/[^\w.\-]/g, '_')}`
  const { error } = await supabase.storage.from(ATTACH_BUCKET).upload(path, file, { contentType: file.type })
  if (error) throw error
  const { data } = await supabase.storage.from(ATTACH_BUCKET).createSignedUrl(path, ATTACH_SIGNED_TTL)
  return { url: data?.signedUrl ?? '', path, name: file.name, mime: file.type, size: file.size, kind, ...dims }
}

export async function fetchBrandRole(brandId: string, userId: string): Promise<string | null> {
  if (isPreviewMode()) return 'admin'
  const { data, error } = await supabase
    .from('brand_user_access')
    .select('role')
    .eq('brand_id', brandId)
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return (data as { role: string } | null)?.role ?? null
}

// ── Writes ───────────────────────────────────────────────────────────────────

// Returns the inserted row so the composer can echo it immediately; the
// realtime INSERT subscription dedupes against it by id.
export async function sendMessage(
  conversationId: string,
  body: string,
  userId: string,
  attachments: Attachment[] = [],
): Promise<Message> {
  if (isPreviewMode()) return mockSendMessage(conversationId, body, attachments)
  const { data, error } = await supabase
    .from('cr_messages')
    .insert({ conversation_id: conversationId, sender_type: 'brand', sender_user_id: userId, body, attachments })
    .select()
    .single()
  if (error) throw error
  return data as Message
}

// Edit a message's body and, when passed, its attachments. Note: the DB CHECK
// requires a non-empty body OR at least one attachment, so callers must not
// save an empty body with no attachments.
export async function editMessage(messageId: string, newBody: string, attachments?: Attachment[]): Promise<void> {
  if (isPreviewMode()) {
    for (const msgs of Object.values(mockMessages)) {
      const m = msgs.find((x) => x.id === messageId)
      if (m) { m.body = newBody; m.edited_at = new Date().toISOString(); if (attachments) m.attachments = attachments }
    }
    return
  }
  const patch: { body: string; edited_at: string; attachments?: Attachment[] } = {
    body: newBody.trim(),
    edited_at: new Date().toISOString(),
  }
  if (attachments) patch.attachments = attachments
  const { error } = await supabase
    .from('cr_messages')
    .update(patch)
    .eq('id', messageId)
  if (error) throw error
}

// cr_messages.body has a CHECK (char_length(body) > 0) constraint, so a
// soft-delete can't blank it to ''. The UI ignores body once is_deleted is
// true (renders a fixed "This message was deleted" label), so any non-empty
// placeholder is safe here.
const DELETED_BODY_PLACEHOLDER = '[deleted]'

export async function deleteMessage(messageId: string): Promise<void> {
  if (isPreviewMode()) {
    for (const msgs of Object.values(mockMessages)) {
      const m = msgs.find((x) => x.id === messageId)
      if (m) { m.is_deleted = true; m.body = DELETED_BODY_PLACEHOLDER; m.deleted_at = new Date().toISOString() }
    }
    return
  }
  const { error } = await supabase
    .from('cr_messages')
    .update({ is_deleted: true, body: DELETED_BODY_PLACEHOLDER, deleted_at: new Date().toISOString() })
    .eq('id', messageId)
  if (error) throw error
}

export async function markConversationRead(conversationId: string, userId: string): Promise<void> {
  if (isPreviewMode()) {
    mockReadStates[conversationId] = { last_read_at: new Date().toISOString(), manually_unread: false }
    return
  }
  const { error } = await supabase
    .from('cr_conversation_reads')
    .upsert(
      { conversation_id: conversationId, user_id: userId, last_read_at: new Date().toISOString(), manually_unread: false },
      { onConflict: 'conversation_id,user_id' },
    )
  if (error) throw error
  // The sidebar's unread badge counts off cr_conversations, which a read does
  // not touch — without this it keeps showing a thread the user just read.
  notifyNavBadgesStale()
}

// Manually flag a thread unread (for follow-up). Cleared on next open.
export async function markUnread(conversationId: string, userId: string): Promise<void> {
  if (isPreviewMode()) {
    const ex = mockReadStates[conversationId]
    mockReadStates[conversationId] = { last_read_at: ex?.last_read_at ?? null, manually_unread: true }
    return
  }
  const { error } = await supabase
    .from('cr_conversation_reads')
    .upsert(
      { conversation_id: conversationId, user_id: userId, manually_unread: true },
      { onConflict: 'conversation_id,user_id' },
    )
  if (error) throw error
  notifyNavBadgesStale()   // this one puts the badge back UP
}

export async function sendBroadcast(
  brandId: string,
  segmentType: 'all_hired' | 'campaign',
  campaignId: string | null,
  body: string,
): Promise<BroadcastResult> {
  if (isPreviewMode()) return mockSendBroadcast(segmentType, campaignId, body)
  const { data, error } = await supabase.rpc('send_broadcast', {
    p_brand_id: brandId,
    p_segment_type: segmentType,
    p_campaign_id: campaignId,
    p_body: body,
  })
  if (error) throw error
  return data as BroadcastResult
}

// ── Derived helpers ──────────────────────────────────────────────────────────

// A brand-side thread is "unread" when it's manually flagged unread, OR the
// latest message came from the creator and the user's read cursor is behind it.
// System seeds / the brand's own sends never auto-count.
type UnreadShape = Pick<Conversation, 'id' | 'last_message_at' | 'last_message_sender_type'>
export function isUnread(c: UnreadShape, states: Record<string, ReadState>): boolean {
  const st = states[c.id]
  if (st?.manually_unread) return true
  if (c.last_message_sender_type !== 'creator' || !c.last_message_at) return false
  return !st?.last_read_at || st.last_read_at < c.last_message_at
}

// True for a thread nobody has actually written in — it holds only seeds and
// other platform-generated cards. Those sort BELOW every real conversation in
// the inbox, so a batch of fresh applications can't bury a thread the brand is
// talking in. Once a person writes one message the thread rejoins the normal
// recency order and keeps its slot forever after, even if a later application
// seed becomes its newest message. A manual "mark as unread" still wins, since
// the unread tier is compared first.
//
// Two signals, because neither is sufficient alone:
//   last_human_message_at — stamped by the insert trigger for ANY non-system
//     sender, so a null is proof nothing but seeds exist (no lookup needed),
//     but a value is NOT proof a person wrote: mass broadcasts and automated
//     deadline nudges are stored as sender_type 'brand' and stamp it too. Those
//     threads read as system-only to the brand (the nudge even renders as a
//     gray system card), which is why keying on this column alone left them
//     above real conversations.
//   humanWritten — the per-thread verdict from
//     fetchHumanWrittenConversationIds below. Undefined until that lands, and
//     an unresolved thread keeps the normal order rather than jumping.
type SystemOnlyShape = Pick<Conversation, 'id' | 'last_message_sender_type' | 'last_human_message_at'>
export function isSystemOnlyThread(c: SystemOnlyShape, humanWritten: Record<string, boolean> = {}): boolean {
  if (!c.last_human_message_at) {
    // Defensive: if the denorm column ever lags a human message, trust the
    // sender type and leave the thread in the normal order.
    return c.last_message_sender_type === 'system' || c.last_message_sender_type === null
  }
  return humanWritten[c.id] === false
}

// Of the given threads, the ones holding at least one message a PERSON typed.
// Same predicate the worker's In-Negotiation filter uses (hiring.ts
// negotiatingApplicationIds) so the two screens agree on what counts:
//   - any 'creator' row — creators can't broadcast and never send automated
//     messages, so every one of theirs is genuine;
//   - a 'brand' row only when broadcast_id IS NULL (a mass broadcast hits every
//     active thread at once, so counting it would un-demote the whole inbox on
//     one send) and by_platform = false (automated nudges are stored as 'brand'
//     so they still notify the creator).
// The brand branch matches idx_cr_messages_brand_authored's predicate exactly
// (migration 20260817050001). Chunked so a long inbox can't build an unbounded
// in.() list; callers only pass threads with last_human_message_at set, since a
// null already settles it.
const HUMAN_WRITTEN_CHUNK = 100
export async function fetchHumanWrittenConversationIds(conversationIds: string[]): Promise<Set<string>> {
  const found = new Set<string>()
  if (conversationIds.length === 0) return found
  if (isPreviewMode()) {
    for (const id of conversationIds) {
      const msgs = mockMessages[id] ?? []
      if (msgs.some((m) => m.sender_type === 'creator'
        || (m.sender_type === 'brand' && !m.broadcast_id && !m.by_platform))) found.add(id)
    }
    return found
  }
  for (let i = 0; i < conversationIds.length; i += HUMAN_WRITTEN_CHUNK) {
    const chunk = conversationIds.slice(i, i + HUMAN_WRITTEN_CHUNK)
    const { data, error } = await supabase
      .from('cr_messages')
      .select('conversation_id')
      .in('conversation_id', chunk)
      .or('sender_type.eq.creator,and(sender_type.eq.brand,broadcast_id.is.null,by_platform.is.false)')
    if (error) throw error
    for (const row of (data ?? []) as { conversation_id: string }[]) found.add(row.conversation_id)
  }
  return found
}

// Lightweight unread tally for the sidebar badge. Counts CREATORS with an unread
// thread, not threads: the inbox lists one row per creator (their per-job chats
// are rolled up behind it), so counting threads here would put a bigger number on
// the nav item than the inbox has rows to show.
export async function countUnread(brandId: string, userId: string): Promise<number> {
  const distinct = (rows: (UnreadShape & { creator_id: string })[], states: Record<string, ReadState>) =>
    new Set(rows.filter((c) => isUnread(c, states)).map((c) => c.creator_id)).size
  if (isPreviewMode()) return distinct(mockSortedConversations(), mockReadStates)
  const states = await fetchReadCursors(userId)
  const { data, error } = await supabase
    .from('cr_conversations')
    .select('id, creator_id, last_message_at, last_message_sender_type')
    .eq('brand_id', brandId)
    .eq('status', 'active')
  if (error) throw error
  return distinct((data ?? []) as (UnreadShape & { creator_id: string })[], states)
}

export function creatorDisplayName(c?: MsgCreator | null, fallback = 'Creator'): string {
  if (!c) return fallback
  return c.name || (c.tiktok_username ? `@${c.tiktok_username}` : fallback)
}

// Job label for a per-job thread; null for legacy combined threads.
export function conversationJobName(c?: Conversation | null): string | null {
  return c?.campaign?.name || c?.campaign?.product_name || null
}

// The "job ended" tag shown on a thread whose invite was cancelled or
// application withdrawn (etc.). Null while the job is active. Clears
// automatically once either side re-engages (see migration 20260715000005).
export function conversationEndedLabel(
  c?: Pick<Conversation, 'ended_at' | 'ended_reason' | 'ended_by'> | null,
): string | null {
  if (!c?.ended_at) return null
  switch (c.ended_reason) {
    case 'withdrawn': return 'Withdrawn by creator'
    case 'cancelled': return 'Cancelled by brand'
    case 'declined': return 'Declined by creator'
    case 'rejected': return 'Declined by brand'
    case 'expired': return 'Offer expired'
    default: return 'Job ended'
  }
}

// ── Local preview mode (no DB / no auth) ─────────────────────────────────────
// Active only on the public /preview/messages route. Lets you click through the
// full UI with realistic data before the schema is provisioned. Pure in-memory;
// nothing touches Supabase. See pages/MessagesPreview.tsx.
export function isPreviewMode(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/preview/')
}

const ago = (mins: number) => new Date(Date.now() - mins * 60000).toISOString()
let mockSeq = 1
const nextId = () => `mock-msg-${mockSeq++}`

const mockCampaigns: CampaignLite[] = [
  { id: 'camp-1', name: 'Summer Glow Launch', product_name: 'Glow Serum', status: 'active' },
  { id: 'camp-2', name: 'Holiday Gift Push', product_name: 'Deluxe Gift Set', status: 'active' },
]

const mockCreators: Record<string, MsgCreator> = {
  ava: { id: 'cr-ava', name: 'Ava Chen', tiktok_username: 'avachen', tiktok_url: null, avatar_url: null, creator_gmv: 184000, gmv_revenue_range: null, ec_video_count: 22, gmv_per_video: 8363 },
  // creator_gmv = GMV_RANGE_SENTINEL_DOLLARS (2500): sales are private, so the
  // pills must show the range string and drop GMV / video entirely.
  marcus: { id: 'cr-marcus', name: 'Marcus Lee', tiktok_username: 'marcusmakes', tiktok_url: null, avatar_url: null, creator_gmv: 2500, gmv_revenue_range: '$1K-$10K', ec_video_count: 14, gmv_per_video: 4464 },
  priya: { id: 'cr-priya', name: 'Priya Nair', tiktok_username: 'priyabeauty', tiktok_url: null, avatar_url: null, creator_gmv: 421000, gmv_revenue_range: null, ec_video_count: 31, gmv_per_video: 13580 },
  // No ec_video_count — exercises the stored-column fallback in gmvPerVideoOf.
  diego: { id: 'cr-diego', name: 'Diego Santos', tiktok_username: 'diegoshoots', tiktok_url: null, avatar_url: null, creator_gmv: 28900, gmv_revenue_range: null, ec_video_count: null, gmv_per_video: 2890 },
}

const mockHireLinks: HireLink[] = [
  { creator_id: 'cr-ava', campaign_id: 'camp-1', status: 'confirmed', videos_required: 10, videos_detected: 3, brand_rate_cents: 55000 },
  { creator_id: 'cr-ava', campaign_id: 'camp-2', status: 'confirmed', videos_required: 5, videos_detected: 1, brand_rate_cents: 51000 },
  { creator_id: 'cr-marcus', campaign_id: 'camp-1', status: 'confirmed', videos_required: 10, videos_detected: 8, brand_rate_cents: 70000 },
  { creator_id: 'cr-priya', campaign_id: 'camp-1', status: 'confirmed', videos_required: 5, videos_detected: 0, brand_rate_cents: 51000 },
  { creator_id: 'cr-diego', campaign_id: 'camp-2', status: 'completed', videos_required: 5, videos_detected: 5, brand_rate_cents: 70000 },
]

// Campaign GMV per `${creator_id}:${campaign_id}` (cents).
const mockCampaignGmv: Record<string, number> = {
  'cr-ava:camp-1': 1240000,
  'cr-ava:camp-2': 310000,
  'cr-marcus:camp-1': 880000,
  'cr-diego:camp-2': 1530000,
}

const campRef = (id: string): Conversation['campaign'] => {
  const c = mockCampaigns.find((x) => x.id === id)
  return c ? { id: c.id, name: c.name, product_name: c.product_name } : null
}

const mockConversations: Conversation[] = [
  {
    id: 'conv-ava', brand_id: 'preview-brand', creator_id: 'cr-ava', campaign_id: 'camp-1', status: 'active',
    last_message_at: ago(8), last_message_preview: 'Sounds great — I’ll have the first cut over by Friday!',
    last_message_sender_type: 'creator', last_human_message_at: ago(8), pinned_at: null, priority_at: null, ended_at: null, ended_reason: null, ended_by: null,
    created_at: ago(4000), updated_at: ago(8), creator: mockCreators.ava, campaign: campRef('camp-1'),
  },
  // Ava again on a second job — per-job threads mean two chats with one creator.
  {
    id: 'conv-ava-holiday', brand_id: 'preview-brand', creator_id: 'cr-ava', campaign_id: 'camp-2', status: 'active',
    last_message_at: ago(45), last_message_preview: 'Just got the gift set in the mail — unboxing idea incoming!',
    last_message_sender_type: 'creator', last_human_message_at: ago(45), pinned_at: null, priority_at: null, ended_at: null, ended_reason: null, ended_by: null,
    created_at: ago(3000), updated_at: ago(45), creator: mockCreators.ava, campaign: campRef('camp-2'),
  },
  // Legacy combined thread from before per-job threads (campaign_id NULL).
  {
    id: 'conv-marcus', brand_id: 'preview-brand', creator_id: 'cr-marcus', campaign_id: null, status: 'active',
    last_message_at: ago(180), last_message_preview: 'Perfect, the affiliate link is in your dashboard.',
    last_message_sender_type: 'brand', last_human_message_at: ago(180), pinned_at: null, priority_at: null, ended_at: null, ended_reason: null, ended_by: null,
    created_at: ago(5000), updated_at: ago(180), creator: mockCreators.marcus, campaign: null,
  },
  {
    id: 'conv-diego', brand_id: 'preview-brand', creator_id: 'cr-diego', campaign_id: 'camp-2', status: 'active',
    last_message_at: ago(1500), last_message_preview: 'Can I get a couple more days for the gift-set video?',
    last_message_sender_type: 'creator', last_human_message_at: ago(1500), pinned_at: null, priority_at: null, ended_at: null, ended_reason: null, ended_by: null,
    created_at: ago(9000), updated_at: ago(1500), creator: mockCreators.diego, campaign: campRef('camp-2'),
  },
  {
    id: 'conv-priya', brand_id: 'preview-brand', creator_id: 'cr-priya', campaign_id: 'camp-1', status: 'active',
    last_message_at: ago(2880), last_message_preview: 'Hire confirmed — Summer Glow Launch',
    last_message_sender_type: 'system', last_human_message_at: null, pinned_at: null, priority_at: null, ended_at: null, ended_reason: null, ended_by: null,
    created_at: ago(2880), updated_at: ago(2880), creator: mockCreators.priya, campaign: campRef('camp-1'),
  },
]

const mockMessagesRaw: Record<string, Omit<Message, 'attachments' | 'edited_at' | 'is_deleted' | 'deleted_at' | 'by_platform' | 'message_category' | 'rate_snapshot' | 'application_id' | 'hire_id' | 'counter_offer_id' | 'counter_offer'>[]> = {
  'conv-ava': [
    { id: 'm-ava-1', conversation_id: 'conv-ava', sender_type: 'system', sender_user_id: null, body: 'Hire confirmed — Summer Glow Launch', broadcast_id: null, campaign_id: 'camp-1', created_at: ago(4000) },
    { id: 'm-ava-2', conversation_id: 'conv-ava', sender_type: 'brand', sender_user_id: 'u1', body: 'Hi Ava! So excited to work with you on the Glow Serum launch. Have you had a chance to look at the brief?', broadcast_id: null, campaign_id: null, created_at: ago(60) },
    { id: 'm-ava-3', conversation_id: 'conv-ava', sender_type: 'creator', sender_user_id: 'u2', body: 'Yes! Love the direction. Thinking a GRWM that works the serum into a morning routine.', broadcast_id: null, campaign_id: null, created_at: ago(30) },
    { id: 'm-ava-4', conversation_id: 'conv-ava', sender_type: 'brand', sender_user_id: 'u1', body: 'That’s perfect. When do you think you could have a first cut ready?', broadcast_id: null, campaign_id: null, created_at: ago(20) },
    { id: 'm-ava-5', conversation_id: 'conv-ava', sender_type: 'creator', sender_user_id: 'u2', body: 'Sounds great — I’ll have the first cut over by Friday!', broadcast_id: null, campaign_id: null, created_at: ago(8) },
  ],
  'conv-ava-holiday': [
    { id: 'm-avah-1', conversation_id: 'conv-ava-holiday', sender_type: 'system', sender_user_id: null, body: 'Hire confirmed — Holiday Gift Push', broadcast_id: null, campaign_id: 'camp-2', created_at: ago(3000) },
    { id: 'm-avah-2', conversation_id: 'conv-ava-holiday', sender_type: 'brand', sender_user_id: 'u1', body: 'Hey Ava, sent the Deluxe Gift Set your way — should arrive this week.', broadcast_id: null, campaign_id: null, created_at: ago(600) },
    { id: 'm-avah-3', conversation_id: 'conv-ava-holiday', sender_type: 'creator', sender_user_id: 'u2', body: 'Just got the gift set in the mail — unboxing idea incoming!', broadcast_id: null, campaign_id: null, created_at: ago(45) },
  ],
  'conv-marcus': [
    { id: 'm-mar-1', conversation_id: 'conv-marcus', sender_type: 'system', sender_user_id: null, body: 'Hire confirmed — Summer Glow Launch', broadcast_id: null, campaign_id: 'camp-1', created_at: ago(5000) },
    { id: 'm-mar-2', conversation_id: 'conv-marcus', sender_type: 'creator', sender_user_id: 'u3', body: 'Hey! Where do I grab the affiliate link once I’m ready to post?', broadcast_id: null, campaign_id: null, created_at: ago(200) },
    { id: 'm-mar-3', conversation_id: 'conv-marcus', sender_type: 'brand', sender_user_id: 'u1', body: 'Perfect, the affiliate link is in your dashboard.', broadcast_id: null, campaign_id: null, created_at: ago(180) },
  ],
  'conv-diego': [
    { id: 'm-die-1', conversation_id: 'conv-diego', sender_type: 'system', sender_user_id: null, body: 'Hire confirmed — Holiday Gift Push', broadcast_id: null, campaign_id: 'camp-2', created_at: ago(9000) },
    { id: 'm-die-2', conversation_id: 'conv-diego', sender_type: 'creator', sender_user_id: 'u4', body: 'Can I get a couple more days for the gift-set video?', broadcast_id: null, campaign_id: null, created_at: ago(1500) },
  ],
  'conv-priya': [
    { id: 'm-pri-1', conversation_id: 'conv-priya', sender_type: 'system', sender_user_id: null, body: 'Hire confirmed — Summer Glow Launch', broadcast_id: null, campaign_id: 'camp-1', created_at: ago(2880) },
  ],
}

const mockMessages: Record<string, Message[]> = Object.fromEntries(
  Object.entries(mockMessagesRaw).map(([k, v]): [string, Message[]] => [k, v.map((m) => ({
    ...m, attachments: [] as Attachment[], edited_at: null, is_deleted: false, deleted_at: null, by_platform: false, message_category: null,
    rate_snapshot: null, application_id: null, hire_id: null, counter_offer_id: null, counter_offer: null,
  }))]),
)

// conv-marcus is read (cursor newer than its last message); the others are not.
const mockReadStates: Record<string, ReadState> = {
  'conv-marcus': { last_read_at: ago(120), manually_unread: false },
}

// Inbox sort: all active threads (incl. apply/invite threads with only the
// system seed), pinned first, then most recent.
function mockSortedConversations(): Conversation[] {
  return [...mockConversations]
    .filter((c) => c.status === 'active')
    .sort((a, b) => {
      if (!!a.pinned_at !== !!b.pinned_at) return a.pinned_at ? -1 : 1
      if (a.pinned_at && b.pinned_at) return b.pinned_at.localeCompare(a.pinned_at)
      return (b.last_message_at ?? '').localeCompare(a.last_message_at ?? '')
    })
}

function mockSendMessage(conversationId: string, body: string, attachments: Attachment[] = []): Message {
  const msg: Message = {
    id: nextId(), conversation_id: conversationId, sender_type: 'brand',
    sender_user_id: 'u1', body, attachments, broadcast_id: null, campaign_id: null,
    created_at: new Date().toISOString(), edited_at: null, is_deleted: false, deleted_at: null,
    by_platform: false, message_category: null,
    rate_snapshot: null, application_id: null, hire_id: null, counter_offer_id: null, counter_offer: null,
  }
  ;(mockMessages[conversationId] ??= []).push(msg)
  const conv = mockConversations.find((c) => c.id === conversationId)
  if (conv) {
    conv.last_message_at = msg.created_at
    conv.last_message_preview = body.slice(0, 140) || attachmentPreview(attachments)
    conv.last_message_sender_type = 'brand'
    conv.last_human_message_at = msg.created_at
  }
  return msg
}

// Label shown in the list when a message is attachment-only (no text body).
export function attachmentPreview(atts: Attachment[]): string {
  if (atts.length === 0) return ''
  if (atts.every((a) => a.kind === 'image')) return atts.length === 1 ? 'Photo' : `${atts.length} photos`
  return atts.length === 1 ? (atts[0].name || 'Attachment') : `${atts.length} attachments`
}

function mockSendBroadcast(
  segmentType: 'all_hired' | 'campaign',
  campaignId: string | null,
  body: string,
): BroadcastResult {
  const active = mockConversations.filter((c) => c.status === 'active')
  const targets = segmentType === 'campaign'
    ? active.filter((c) => mockHireLinks.some((h) => h.campaign_id === campaignId && h.creator_id === c.creator_id))
    // 'all_hired' → only creators with a live hire (fans into each hired thread).
    : active.filter((c) => mockHireLinks.some((h) => h.creator_id === c.creator_id))
  for (const conv of targets) mockSendMessage(conv.id, body)
  // recipient_count = distinct creators reached (mirrors the server).
  return { broadcast_id: 'mock-broadcast', recipient_count: new Set(targets.map((c) => c.creator_id)).size }
}
